import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Sparkles, Archive, ArchiveRestore } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../store/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import NewFromTemplateModal from "../components/NewFromTemplateModal";
import NewBlankModal from "../components/NewBlankModal";
import { toast } from "sonner";

const STATUS_LABELS = { draft: "Borrador", review: "En revisión", approved: "Aprobado", archived: "Archivado", obsolete: "Obsoleto" };
const TYPE_LABELS = { policy: "Política", procedure: "Procedimiento", record: "Registro", soa: "SoA", risk_assessment: "Análisis de riesgos", plan: "Plan" };

function statusBadge(status) {
  const variants = { draft: "secondary", review: "outline", approved: "default", archived: "outline", obsolete: "secondary" };
  const cls = {
    draft: "bg-slate-100 text-slate-700",
    review: "bg-amber-100 text-amber-800 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    archived: "bg-indigo-100 text-indigo-700 border-indigo-200",
    obsolete: "bg-slate-100 text-slate-400 line-through",
  };
  return <Badge variant={variants[status]} className={cls[status]}>{STATUS_LABELS[status]}</Badge>;
}

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-ES"); } catch { return d; }
}

function needsReviewSoon(d) {
  if (!d) return false;
  const next = new Date(d);
  const limit = new Date(); limit.setDate(limit.getDate() + 30);
  return next < limit;
}

export default function Documents() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = !["auditor", "member"].includes(user?.role);
  const canArchive = ["owner", "admin", "compliance_manager"].includes(user?.role);

  const [filters, setFilters] = useState({ type: "all", status: "all", control: "" });
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tplOpen, setTplOpen] = useState(false);
  const [blankOpen, setBlankOpen] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ page: String(page), page_size: "25" });
        if (filters.type !== "all") qs.set("type", filters.type);
        if (filters.status !== "all") qs.set("status", filters.status);
        if (includeArchived) qs.set("include_archived", "true");
        if (filters.control.trim()) qs.set("related_control_code", filters.control.trim().toUpperCase());
        const { data } = await api.get(`/documents?${qs}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters, page, includeArchived, reloadFlag]);

  const archive = async (e, id) => {
    e.stopPropagation();
    try { await api.post(`/documents/${id}/archive`); toast.success("Documento archivado."); setReloadFlag((f) => f + 1); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unarchive = async (e, id) => {
    e.stopPropagation();
    try { await api.post(`/documents/${id}/unarchive`); toast.success("Documento desarchivado."); setReloadFlag((f) => f + 1); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div data-testid="documents-page" className="max-w-7xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-slate-900">Documentos</h1>
          <p className="text-[14px] text-slate-500 mt-1">Políticas, procedimientos, registros y planes del SGSI.</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setBlankOpen(true)} data-testid="btn-new-blank">
              <Plus className="h-4 w-4 mr-1" /> Nuevo en blanco
            </Button>
            <Button onClick={() => setTplOpen(true)} data-testid="btn-new-from-template">
              <Sparkles className="h-4 w-4 mr-1" /> Nuevo desde plantilla
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-3 flex-wrap">
        <Select value={filters.type} onValueChange={(v) => { setFilters({ ...filters, type: v }); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => { setFilters({ ...filters, status: v }); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          className="w-48"
          placeholder="Control (ej. A.5.1)"
          value={filters.control}
          onChange={(e) => setFilters({ ...filters, control: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && setPage(1)}
          data-testid="filter-control"
        />
        <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer ml-1">
          <Checkbox checked={includeArchived} onCheckedChange={(v) => { setIncludeArchived(!!v); setPage(1); }} data-testid="toggle-archived" />
          Ver archivados
        </label>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-center">v</TableHead>
              <TableHead>Controles</TableHead>
              <TableHead>Última modificación</TableHead>
              <TableHead>Próxima revisión</TableHead>
              {canArchive && <TableHead className="text-right">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={canArchive ? 8 : 7} className="text-center text-slate-500 py-8">Cargando...</TableCell></TableRow>}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={canArchive ? 8 : 7} className="text-center text-slate-500 py-10">
                Sin documentos. {canCreate && "Crea uno con una plantilla para empezar."}
              </TableCell></TableRow>
            )}
            {!loading && data.items.map((d) => {
              const ctrls = d.related_controls || [];
              const visible = ctrls.slice(0, 3);
              const extra = ctrls.length - visible.length;
              return (
                <TableRow key={d.id} onClick={() => navigate(`/documents/${d.id}`)} className="cursor-pointer hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {d.generated_by_ai ? <Sparkles className="h-3.5 w-3.5 text-primary" /> : <FileText className="h-3.5 w-3.5 text-slate-400" />}
                      <span className="font-medium text-slate-900">{d.title}</span>
                      {d.source === "imported" && (
                        <Badge className="bg-violet-100 text-violet-700 border-violet-200" data-testid={`imported-badge-${d.id}`}>Importado</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-[12px]">{TYPE_LABELS[d.type] || d.type}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {statusBadge(d.status)}
                      {d.status === "approved" && needsReviewSoon(d.next_review_date) && (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">Revisión pendiente</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-slate-700 text-[12px]">{d.current_version}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {visible.map((c) => <span key={c} className="text-[11px] border border-slate-200 rounded px-1.5 py-0.5 text-slate-600">{c}</span>)}
                      {extra > 0 && <span className="text-[11px] text-slate-400">+{extra}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-[12px]">{fmt(d.updated_at)}</TableCell>
                  <TableCell className="text-slate-500 text-[12px]">{fmt(d.next_review_date)}</TableCell>
                  {canArchive && (
                    <TableCell className="text-right">
                      {d.status === "approved" && (
                        <Button variant="ghost" size="sm" onClick={(e) => archive(e, d.id)} title="Archivar" data-testid={`doc-archive-${d.id}`}>
                          <Archive className="h-4 w-4 text-indigo-500" />
                        </Button>
                      )}
                      {d.status === "archived" && (
                        <Button variant="ghost" size="sm" onClick={(e) => unarchive(e, d.id)} title="Desarchivar" data-testid={`doc-unarchive-${d.id}`}>
                          <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4 text-[12px] text-slate-500">
        <span>Total: {data.total} · Página {page} de {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="docs-prev">Anterior</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="docs-next">Siguiente</Button>
        </div>
      </div>

      {tplOpen && <NewFromTemplateModal open={tplOpen} onClose={() => setTplOpen(false)} onCreated={(d) => navigate(`/documents/${d.id}`)} />}
      {blankOpen && <NewBlankModal open={blankOpen} onClose={() => setBlankOpen(false)} onCreated={(d) => navigate(`/documents/${d.id}`)} />}
    </div>
  );
}
